import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import {
  BarChart3,
  Search,
  MapPin,
  Globe2,
  CheckCircle2,
  TrendingUp,
  Users,
  Phone,
  Mail,
  MessageCircle,
  ArrowRight,
  CreditCard,
  Megaphone,
  ChevronRight,
  ChevronLeft,
  Facebook,
  Palette,
  Brain
} from "lucide-react";
import { motion } from "framer-motion";
import { useRef } from "react";
import heroImage from "@assets/tom_and_kayley_1773661860045.webp";
import spoonerLogo from "@assets/spooner-logo_1776849762461_1776853870067.webp";

export default function Home() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      icon: <Search className="w-8 h-8 text-primary" />,
      title: "SEO",
      description: "Get your business to page one with tried and tested SEO strategies that drive results.",
      href: "/search-engine-optimisation.html"
    },
    {
      icon: <Brain className="w-8 h-8 text-primary" />,
      title: "AI SEO",
      description: "Future-proof your online presence with AI-powered SEO, Geographic optimisation, and Answer Engine Optimisation.",
      href: "/ai-seo.html"
    },
    {
      icon: <CreditCard className="w-8 h-8 text-primary" />,
      title: "PPC Management",
      description: "We offer pay-per-click management services that will help your business successfully utilise the power of paid marketing.",
      href: "/pay-per-click-management.html"
    },
    {
      icon: <Megaphone className="w-8 h-8 text-primary" />,
      title: "Social Media Management",
      description: "Build brand awareness and drive engagement with strategic social media campaigns across all platforms.",
      href: "/social-media-management.html"
    },
    {
      icon: <Facebook className="w-8 h-8 text-primary" />,
      title: "Facebook Ads",
      description: "Reach billions of targeted users with strategic Facebook and Instagram advertising campaigns.",
      href: "/facebook-advertising.html"
    },
    {
      icon: <Palette className="w-8 h-8 text-primary" />,
      title: "Web Design",
      description: "Beautiful, responsive websites designed to attract visitors and convert them into customers.",
      href: "/web-design.html"
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>SEO Cornwall | The Cornwall SEO Co.</title>
        <meta name="description" content="The Cornwall SEO Co. provides high quality SEO services in Cornwall. We boost online presence through SEO, Social Media, Content and PPC Management." />
      </Helmet>
      <Navbar />

      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage} 
            alt="Tom & Kayley in SEO Meeting" 
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="inline-block bg-primary px-4 py-1 mb-6 mt-4 md:mt-0">
              <span className="text-black font-bold uppercase tracking-widest text-sm">Digital Marketing Agency</span>
            </div>
            <h1 className="text-7xl md:text-8xl font-heading font-black text-white mb-8 leading-none uppercase italic tracking-tighter" style={{ fontSize: 'clamp(2.75rem, 8vw, 5.25rem)' }}>
              The Cornwall SEO Co <br />
              <span className="text-primary">Proper Job</span>
            </h1>
            <p className="text-xl text-white/90 mb-10 max-w-3xl leading-relaxed font-medium">
              We set up The Cornwall SEO Co to assist local businesses. It's great to meet face to face – we welcome clients who would prefer to come in to discuss their campaign. Our experts are here to help you reach your objectives. Call us on 01726 74161, email us or even pop into the office – we look forward to meeting you!
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                href="/contact.html"
                className="inline-flex items-center justify-center bg-primary text-black hover:bg-white text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all"
              >
                Contact Us <ArrowRight className="ml-2" />
              </Link>
              <Link 
                href="/testimonials.html"
                className="inline-flex items-center justify-center text-white border border-white hover:bg-white/10 text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all mb-5 md:mb-0"
              >
                Testimonials
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-10 bg-black border-y border-primary/20">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-between items-center gap-8">
             <div className="text-white font-black italic text-2xl">100% CORNWALL-BASED TEAM</div>
             <div className="text-white font-black italic text-2xl">NO MINIMUM TERM CONTRACT</div>
             <div className="text-white font-black italic text-2xl">COMPETITIVE HOURLY RATE</div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-b-2 border-black overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 mb-12">
            <div className="flex items-center gap-3">
              <span className="text-2xl md:text-3xl font-black text-black uppercase italic">Excellent</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="w-8 h-8 md:w-10 md:h-10 text-[#fbbc04] fill-current" viewBox="0 0 24 24">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
              </div>
            </div>
            <div className="hidden md:block w-2 h-2 rounded-full bg-black/20"></div>
            <a 
              href="https://maps.app.goo.gl/hqhkr2KMmzUeygEHA" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer group"
            >
              <span className="text-lg md:text-xl font-bold text-black/70 uppercase group-hover:text-black transition-colors">See our</span>
              <img src="/google-logo.svg" alt="Google" className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-full p-1 shadow-sm group-hover:shadow-md transition-shadow" />
              <span className="text-xl md:text-2xl font-black text-black uppercase italic">Reviews</span>
            </a>
          </div>

          {/* Reviews Carousel */}
          <div className="flex items-center gap-2 md:gap-4 max-w-7xl mx-auto">
            {/* Left Scroll Indicator */}
            <button 
              className="hidden md:flex flex-shrink-0 w-12 h-12 bg-white border-2 border-black items-center justify-center cursor-pointer shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:bg-primary transition-colors z-10" 
              onClick={() => {
                const container = document.getElementById('reviews-container');
                if (container) {
                  const scrollAmount = container.clientWidth;
                  container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
                }
              }}
              aria-label="Previous review"
            >
              <ChevronLeft className="text-black" />
            </button>

            <div id="reviews-container" className="flex-1 flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-6 pb-12 pt-4 px-2 md:px-0">
              {[
                {
                  name: "Steve Smith",
                  text: "The go-to team for website security, optimisation, and all-around support. Justin and the team exceeded our expectations and were incredibly responsive during a recent tight situation. They truly saved our bacon (or tofu!). Highly recommended."
                },
                {
                  name: "Joanne Clement",
                  text: "I have worked closely with this company for the past couple of months and will continue to use every month. Justin, Danny and Tom have helped me work on my website issues, SEO and Google Adwords. They are all very professional and very quick at replying and helpful at explaining everything for someone like me who does not really understand it all. I would highly recommend using them as my website is now being seen by customers and my bookings have increased :) Huge benefit to any business."
                },
                {
                  name: "Johanna Rogers",
                  text: "A brilliant company who are quick to help and respond. In a short time they increased my rankings on google, I now have a full diary of clients. A massive thanks to Justin and the team."
                },
                {
                  name: "Annie's Taxis & Tours Bodmin Parkway",
                  text: "Justin has been so helpful and patient. Very responsive and professional in helping with my website and Google Account. I highly recommend this company."
                },
                {
                  name: "Peter Gardiner",
                  text: "Top company, all the staff are very professional, helpfull and giving me great customer service I am now getting great results with the SEO."
                },
                {
                  name: "Shaun King Vehicle Engineering",
                  text: "We have been working with Justin at Page1 for over two years now. Our website needed a complete redesign and improved online visibility. Throughout the build their expertise became very apparent and communication from Justin was extremely professional with customer focus at the forefront. We are extremely happy with the end result; the site is user friendly and now reflects perfectly all aspects of work we undertake. The support and service we received throughout the build and since has been really impressive and the reason we would not hesitate to recommend Justin and the team to anyone considering improvements to their business online presence."
                }
              ].map((review, i) => (
                <div key={i} className="w-[85vw] md:w-[calc(50%-12px)] lg:w-[calc(50%-12px)] flex-shrink-0 snap-start h-auto">
                  <div className="bg-white border-2 border-black p-6 md:p-8 h-full shadow-[8px_8px_0px_rgba(0,0,0,0.1)] hover:shadow-[12px_12px_0px_rgba(250,220,32,0.3)] transition-all flex flex-col">
                    <div className="flex items-start md:items-center justify-between mb-6 gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center font-black text-xl italic flex-shrink-0">
                          {review.name.charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-black text-black uppercase italic text-lg leading-tight whitespace-normal">{review.name}</p>
                        </div>
                      </div>
                      <img src="/google-logo.svg" alt="Google" className="w-6 h-6 flex-shrink-0" />
                    </div>
                    <div className="flex gap-1 mb-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg key={star} className="w-5 h-5 text-[#fbbc04] fill-current flex-shrink-0" viewBox="0 0 24 24">
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-black/80 font-medium leading-relaxed whitespace-normal break-words flex-1">
                      "{review.text}"
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Right Scroll Indicator */}
            <button 
              className="hidden md:flex flex-shrink-0 w-12 h-12 bg-white border-2 border-black items-center justify-center cursor-pointer shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:bg-primary transition-colors z-10" 
              onClick={() => {
                const container = document.getElementById('reviews-container');
                if (container) {
                  const scrollAmount = container.clientWidth;
                  container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                }
              }}
              aria-label="Next review"
            >
              <ChevronRight className="text-black" />
            </button>
          </div>
          
          <style dangerouslySetInnerHTML={{__html: `
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .hide-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .custom-scrollbar::-webkit-scrollbar {
              width: 4px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: transparent;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: rgba(0,0,0,0.1);
              border-radius: 4px;
            }
            .custom-scrollbar:hover::-webkit-scrollbar-thumb {
              background: rgba(0,0,0,0.2);
            }
          `}} />
        </div>
      </section>

      <section id="services" className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Performance <br/> <span className="text-primary">Driven Services</span>
            </h2>
            <p className="text-muted-foreground text-xl font-medium">
              Strategic digital marketing solutions tailored to your business goals.
            </p>
          </motion.div>

          <div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  {service.href.startsWith('/') ? (
                    <Link href={service.href} className="block h-full no-underline">
                        <Card className="h-full rounded-none border-2 border-black hover:shadow-[8px_8px_0px_rgba(250,220,32,0.3)] hover:bg-primary transition-all group p-6 cursor-pointer overflow-y-hidden">
                          <CardHeader className="p-0 mb-6">
                            <div className="w-16 h-16 bg-black flex items-center justify-center mb-6 group-hover:bg-white transition-colors">
                              {service.icon}
                            </div>
                            <CardTitle className="font-heading font-black text-xl text-black uppercase italic leading-tight">{service.title}</CardTitle>
                          </CardHeader>
                          <CardContent className="p-0">
                            <p className="text-black font-medium leading-relaxed text-sm">
                              {service.description}
                            </p>
                          </CardContent>
                        </Card>
                    </Link>
                  ) : (
                    <Card className="h-full rounded-none border-2 border-black hover:shadow-[8px_8px_0px_rgba(250,220,32,0.3)] hover:bg-primary transition-all group p-6 overflow-y-hidden">
                      <CardHeader className="p-0 mb-6">
                        <div className="w-16 h-16 bg-black flex items-center justify-center mb-6 group-hover:bg-white transition-colors">
                          {service.icon}
                        </div>
                        <CardTitle className="font-heading font-black text-xl text-black uppercase italic leading-tight">{service.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <p className="text-black font-medium leading-relaxed text-sm">
                          {service.description}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How We Get You to the Top */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              We get you to <br/> <span className="text-primary">the top by being:</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              {
                number: "01",
                title: "Brand Cohesive",
                description: "Your brand is your identity. Our services and strategies will help keep your brand growing further. Reach your brand's full online potential and expand your limits. Consented by you, communication will be cohesive as in all of your other marketing material."
              },
              {
                number: "02",
                title: "Organic",
                description: "Our optimisation strategies are organic. We don't use manipulative SEO practices such as link building, which can propel your website to the top but damage your credibility in the long run."
              },
              {
                number: "03",
                title: "Adjustable to new SEO Algorithms",
                description: "Google is constantly moving the goalposts and we adjust accordingly to the dictated guidelines. By providing you with a clear understanding on these updates, we can dynamically create strategies that conform to the changes. Put simply, your website will always be on the better side of Google."
              },
              {
                number: "04",
                title: "A Business Partner",
                description: "We believe that the foundation of a good business relationship is built on trust and open communication. At The Cornwall SEO Co, we will be there for you until we have achieved your goals and we are fully committed to providing the very best aftercare to all of our customers. Together, we will climb the search engine mountain."
              },
              {
                number: "05",
                title: "100% White Hat",
                description: "For us, there is no other kind of SEO. The key to lasting, long term results is White Hat. By strictly following Google's guidelines, we will protect your investment."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-black p-8 border-2 border-black hover:border-primary transition-colors group"
              >
                <div className="text-primary font-heading font-black text-5xl mb-4 opacity-50 group-hover:opacity-100 transition-opacity italic">
                  {item.number}
                </div>
                <h3 className="text-white font-heading font-black text-xl mb-4 uppercase italic">
                  {item.title}
                </h3>
                <p className="text-white/70 font-medium text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeIn}>
              <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-8 uppercase italic tracking-tighter">
                Why work with <br/> <span className="text-primary">us?</span>
              </h2>
              <div className="flex items-start gap-4 bg-primary/10 p-5 border-l-8 border-primary rounded-none shadow-[6px_6px_0px_rgba(0,0,0,0.08)] mb-8">
                <CheckCircle2 className="text-primary shrink-0 mt-0.5" size={22} />
                <span className="font-semibold text-black leading-relaxed text-base md:text-lg tracking-normal normal-case italic">
                  In short, we get results.
                </span>
              </div>
              <div className="space-y-4 mb-10">
                {[
                  "We are not a start up but a business which has been trading since 2011.",
                  "We have the experts, who have the experience of working in the industry for a number of years.",
                  "We have watched Google change and learnt from those changes to develop a methodology that protects a clients investment.",
                  "We have Google Partner Status and Bing Ads Accreditation.",
                  "We have the testimonials to back up our claims."
                ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-primary/10 p-5 border-l-8 border-primary rounded-none shadow-[6px_6px_0px_rgba(0,0,0,0.08)]">
                  <CheckCircle2 className="text-primary shrink-0 mt-0.5" size={22} />
                  <span className="font-semibold text-black leading-relaxed text-base md:text-lg tracking-normal normal-case italic">
                    {item}
                  </span>
                </div>
              ))}
              </div>

              <Link href="/testimonials.html" className="inline-block">
                  <Button className="bg-black text-white hover:bg-primary hover:text-black rounded-none px-10 py-8 text-xl font-black uppercase italic cursor-pointer">
                    Our Testimonials
                  </Button>
              </Link>
            </motion.div>

            <div className="grid grid-cols-2 gap-4">
               <div className="bg-black aspect-square p-8 flex flex-col justify-end group">
                  <TrendingUp size={48} className="text-primary mb-4" />
                  <p className="text-white font-black uppercase italic text-2xl leading-none">ROI<br/>Focused</p>
               </div>
               <div className="bg-primary aspect-square p-8 flex flex-col justify-end">
                  <Users size={48} className="text-black mb-4" />
                  <p className="text-black font-black uppercase italic text-2xl leading-none">Local<br/>Expertise</p>
               </div>
               <div className="bg-primary aspect-square p-8 flex flex-col justify-end">
                  <Search size={48} className="text-black mb-4" />
                  <p className="text-black font-black uppercase italic text-2xl leading-none">Data<br/>Driven</p>
               </div>
               <div className="bg-black aspect-square p-8 flex flex-col justify-end group">
                  <Globe2 size={48} className="text-primary mb-4" />
                  <p className="text-white font-black uppercase italic text-2xl leading-none">Cornish<br/>Team</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="py-16 md:py-24 bg-primary">
        <div className="container mx-auto px-4 md:px-6">
          <div className="bg-black p-4 md:p-20 shadow-[20px_20px_0px_0px_rgba(255,255,255,0.3)]">
            <div className="grid md:grid-cols-2 gap-6 md:gap-16">
              <div className="mb-6 md:mb-0">
                <h3 className="text-2xl md:text-6xl font-heading font-black text-primary mb-4 md:mb-6 uppercase italic tracking-tighter">Start Your <br/> Journey.</h3>
                <p className="text-white/80 text-sm md:text-xl mb-6 md:mb-12 font-medium">
                  Ready to see what digital marketing can do for your business? Get in touch today.
                </p>
                
                <div className="space-y-4 md:space-y-8">
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Phone size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl">01726 74161</span>
                   </div>
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Mail size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <a href="mailto:hello@thecornwallseoco.co.uk" className="text-white font-black uppercase italic text-xs md:text-xl break-all hover:text-primary transition-colors">hello@thecornwallseoco.co.uk</a>
                   </div>
                   <a
                     href="https://wa.me/447792646061"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center gap-3 md:gap-6 hover:text-primary transition-colors"
                   >
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <MessageCircle size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl hover:text-primary transition-colors">WhatsApp</span>
                   </a>
                </div>
              </div>
              
              <form action="/contact.php" method="POST" className="space-y-3 md:space-y-4">
                <Input name="name" required placeholder="NAME" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="email" required type="email" placeholder="EMAIL" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="website" placeholder="WEBSITE" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Textarea name="message" required placeholder="HOW CAN WE HELP YOU?" className="bg-white border-none rounded-none h-24 md:h-32 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Button type="submit" className="w-full bg-primary text-black hover:bg-white h-14 md:h-16 font-black uppercase italic text-lg md:text-xl rounded-none transition-all cursor-pointer">
                  GO
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
